import type { Store } from 'redux';
import qs from 'qs';
import { HttpHandler } from './http-handler';
import { clearAccessToken, setIsLogin } from '../store/reducer/auth';

type Options = {
  method: string;
  contentType?: string;
};

type JSONobj =
  | {
      [key: string]: string | number | boolean | undefined | null | JSONobj | JSONobj[] | Blob;
    }
  | JSONobj[];

const LOGIN_PATH = '/login';
let isRedirectingToLogin = false;

const clearPersistedAuth = () => {
  if (typeof window === 'undefined') return;

  try {
    const persistedRoot = window.localStorage.getItem('persist:root');

    if (!persistedRoot) return;

    const root = JSON.parse(persistedRoot);

    if (!root.auth) return;

    const auth = JSON.parse(root.auth);
    root.auth = JSON.stringify({
      ...auth,
      accessToken: undefined,
      refreshToken: undefined,
      userId: undefined,
      isLogin: false,
    });

    window.localStorage.setItem('persist:root', JSON.stringify(root));
  } catch {
    window.localStorage.removeItem('persist:root');
  }
};

export class HttpRequest {
  store?: Store;
  prefix = '';
  host = process.env.REACT_APP_API_URL;
  errorHandler?: (statusCode: number, error: HttpHandler) => void;

  constructor(
    store?: Store,
    host: string | null = '',
    prefix: string | null = '',
    errorHandler?: (statusCode: number, error: HttpHandler) => void
  ) {
    this.store = store;
    this.host = process.env.REACT_APP_API_URL!;
    this.prefix = prefix || '';
    this.errorHandler = errorHandler;
  }

  private getHeaders(contentType?: string) {
    const headers: Record<string, string> = {};

    if (!this.store) return headers;

    const state = this.store.getState();

    if (state.auth?.accessToken) {
      headers.authorization = `Bearer ${state.auth.accessToken}`;
    }

    if (state.auth?.deviceToken) {
      headers['X-Device'] = state.auth.deviceToken;
    }

    if (contentType !== 'multipart/form-data') {
      headers.Accept = 'application/json';
      headers['Content-Type'] = 'application/json; charset=utf-8';
    }

    return headers;
  }

  private buildUrl(api: string, data?: JSONobj, method?: string) {
    const queryString =
      method === 'GET' && data ? qs.stringify(data, { addQueryPrefix: true }) : '';
    return `${this.host}${this.prefix}${api}${queryString}`;
  }

  private handleUnauthorized() {
    if (this.store) {
      this.store.dispatch(clearAccessToken());
      this.store.dispatch(setIsLogin(false));
    }

    clearPersistedAuth();

    if (
      typeof window !== 'undefined' &&
      window.location.pathname !== LOGIN_PATH &&
      !isRedirectingToLogin
    ) {
      isRedirectingToLogin = true;
      window.location.replace(LOGIN_PATH);
    }
  }

  async request(api: string, data: Record<string, any>, options: Options) {
    const url = this.buildUrl(api, data, options.method);

    const headers: HeadersInit = this.getHeaders(options.contentType);

    // console.log('headers :', headers);

    const fetchOptions: RequestInit = {
      method: options.method,
      credentials: 'include',
      headers: {
        ...headers,
      },
    };

    if (options.method !== 'GET' && data) {
      if (options.contentType === 'multipart/form-data') {
        const formData = new FormData();
        Object.entries(data).forEach(([key, value]) => {
          formData.append(key, value as any);
        });
        fetchOptions.body = formData;
        delete (fetchOptions.headers as any)['Content-Type'];
      } else {
        fetchOptions.body = JSON.stringify(data);
        (fetchOptions.headers as any)['Content-Type'] = 'application/json; charset=utf-8';
      }
    }

    try {
      const res = await fetch(url, fetchOptions);
      if (res.status === 401) {
        this.handleUnauthorized();
      }

      const http = new HttpHandler(res.status);
      return await http.handle(res);
    } catch (error: any) {
      if (this.errorHandler) {
        this.errorHandler((error as HttpHandler).statusCode, error as HttpHandler);
        return null;
      }
      throw error;
    }
  }

  get(api: string, data?: JSONobj) {
    return this.request(api, data || {}, { method: 'GET' });
  }

  post(api: string, data?: JSONobj) {
    return this.request(api, data || {}, { method: 'POST' });
  }

  put(api: string, data?: JSONobj) {
    return this.request(api, data || {}, { method: 'PUT' });
  }

  del(api: string, data?: JSONobj) {
    return this.request(api, data || {}, { method: 'DELETE' });
  }

  upload(api: string, data?: any) {
    return this.request(api, data || {}, {
      method: 'POST',
      contentType: 'multipart/form-data',
    });
  }

  async blob(api: string, data?: JSONobj) {
    if (!this.store) {
      throw new Error('No store found');
    }

    const headers = this.getHeaders();

    const url = this.buildUrl(api, data || {}, 'GET');

    const fetchOptions: RequestInit = {
      method: 'GET',
      credentials: 'include',
      headers,
    };

    const res = await fetch(url, fetchOptions);
    if (res.status === 401) {
      this.handleUnauthorized();
    }

    return res;
  }
}
